/**
 * The single place a CLOUD timestamp becomes a number.
 *
 * Every `DateTime` column in the cloud is naive: the values are UTC but the column does
 * not say so. WS-D1 made the serializer always append `Z` (`app/core/timefmt.iso_utc`),
 * but a desktop in the field talks to whatever cloud is deployed, and a zone-less string
 * is read by `Date.parse` as LOCAL time (ES2015+). For an operator at +05:30 that shifted
 * every job/log/brand/version timestamp by 5.5 hours: `fmtAgo` clamps at
 * `Math.max(0, now - ms)`, so everything read "just now" forever (AUD-4 LB-4).
 *
 * One function, one rule: **no designator means UTC**.
 */

/** Anything after the time carrying Z / +hh:mm / -hhmm. */
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** ISO-8601 (cloud) string → epoch ms. `0` when absent or unparseable — every caller's
 *  view-model field is a number, and 0 already means "no timestamp" there. */
export function parseServerTs(iso: string | null | undefined): number {
  if (!iso) return 0;
  const trimmed = iso.trim();
  const ms = Date.parse(HAS_ZONE.test(trimmed) ? trimmed : `${trimmed}Z`);
  return Number.isFinite(ms) ? ms : 0;
}

/** The `Date` form, for `toLocaleString()` call sites. `null` when unusable. */
export function serverTsDate(iso: string | null | undefined): Date | null {
  const ms = parseServerTs(iso);
  return ms ? new Date(ms) : null;
}
